<script>
import axios from '@/utils/axios';

export default {
  inject: ['setLoading'],
  data() {
    return {
      showPassword: false,
      email: '',
      password: '',
      errors: {},

      // Brand letters (animated one by one; arrow is drawn over the "g")
      brandLetters: [
        { ch: 'A' }, { ch: 'l' }, { ch: 'g' }, { ch: 'o' },
        { ch: 'C', accent: true }, { ch: 'R', accent: true }, { ch: 'M', accent: true },
        { ch: '.', accent: true }, { ch: 'i', accent: true }, { ch: 'n', accent: true },
      ],

      // Static content (no duplicated/moving cards)
      features: [
        { icon: 'ti-target-arrow', title: 'Target & Convert', text: 'Turn more leads into loyal customers.' },
        { icon: 'ti-layout-dashboard', title: 'Smart Dashboard', text: 'All your key metrics at a glance.' },
        { icon: 'ti-radar-2', title: 'Track Every Lead', text: 'Real-time updates for your sales team.' },
        { icon: 'ti-bolt', title: 'Close Deals Faster', text: 'Smart follow-ups and automation.' },
      ],
      stats: [
        { icon: 'ti-headset', value: '24/7', label: 'Support' },
        { icon: 'ti-activity-heartbeat', value: '99.9%', label: 'Uptime' },
        { icon: 'ti-shield-lock', value: 'Secure', label: '& Private' },
      ],
    };
  },
  computed: {
    buttonLabel() {
      return this.showPassword ? 'Hide' : 'Show';
    },
  },
  mounted() {
    this.updateBodyClass();
  },
  watch: {
    // Watch for route changes
    $route() {
      this.updateBodyClass();
    },
  },
  methods: {
    updateBodyClass() {
      if (this.$route.path.includes('/')) {
        document.body.classList.add('bg-white');
      } else {
        document.body.classList.remove('bg-white');
      }
    },

    toggleShow() {
      this.showPassword = !this.showPassword;
    },

    async submitForm() {
      this.errors = {};
      this.setLoading(true);
      try {
        const response = await axios.post('/login', {
          email: this.email,
          password: this.password,
        });

        const user = response.data.data.user;
        const token = response.data.data.token;

        const client_admin = response.data.data.client_admin;
        const client_setting = response.data.data.client_setting;

        localStorage.setItem('auth_token', token);
        localStorage.setItem('auth_user', JSON.stringify(user));
        localStorage.setItem('client_admin', JSON.stringify(client_admin));
        localStorage.setItem('client_setting', JSON.stringify(client_setting));

        this.$swalToast(`Welcome Back, ${user?.name || 'User'}`, 'success', 4000);
        //REDIRECT TO DASHBOARD
        this.$router.push('/dashboard');
      } catch (error) {
        if (error.type === 'validation') {
          this.errors = error.errors;
        } else {
          this.$swalToast(error.message, 'error');
        }
      } finally {
        this.setLoading(false);
      }
    },
  },
};
</script>

<template>
  <div class="aa-page container-fluid p-0">
    <div class="row g-0 min-vh-100">
      <!-- ============ LEFT: HERO ============ -->
      <aside class="col-lg-6 d-none d-lg-flex aa-hero" aria-label="About AlgoCRM">
        <span class="aa-blob aa-blob-mint" aria-hidden="true"></span>
        <span class="aa-blob aa-blob-violet" aria-hidden="true"></span>

        <div class="aa-hero-inner">
          <div class="aa-brand">
            <span class="aa-brand-name" role="img" aria-label="AlgoCRM.in">
              <span
                v-for="(l, i) in brandLetters"
                :key="i"
                class="aa-letter"
                :class="{ 'is-accent': l.accent }"
                :style="{ '--i': i }"
                aria-hidden="true"
              >{{ l.ch }}<svg
                  v-if="i === 2"
                  class="aa-arrow"
                  viewBox="0 0 34 22"
                  fill="none"
                  aria-hidden="true"
                >
                  <defs>
                    <linearGradient id="aaArrowGrad" x1="0" y1="1" x2="1" y2="0">
                      <stop offset="0" stop-color="#5eead4" />
                      <stop offset="0.5" stop-color="#4f7cff" />
                      <stop offset="1" stop-color="#7c5cff" />
                    </linearGradient>
                  </defs>
                  <path
                    pathLength="1"
                    d="M2 19 L11 10 L16 14.5 L30 3 M21 3 L30 3 L30 12"
                    stroke="url(#aaArrowGrad)"
                    stroke-width="2.6"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg></span>
            </span>
          </div>

          <h1 class="aa-hero-title">One Platform to Manage Leads, Customers &amp; Deals.</h1>
          <p class="aa-hero-text">
            Give your team one place to follow every lead, nurture every customer and close
            more deals, with less manual work.
          </p>

          <!-- CSS dashboard illustration (decorative) -->
          <div class="aa-mock-wrap" aria-hidden="true">
            <div class="aa-chip aa-chip-a"><i class="ti ti-user-plus"></i> New lead added</div>
            <div class="aa-chip aa-chip-b"><i class="ti ti-circle-check"></i> Deal won</div>
          <div class="aa-mock">
            <div class="aa-mock-bar">
              <span></span><span></span><span></span>
              <em class="aa-live"><b></b> Live</em>
            </div>
            <div class="aa-mock-body">
              <div class="aa-mock-kpis">
                <div><small>New leads</small><strong>128</strong></div>
                <div><small>Follow-ups</small><strong>42</strong></div>
                <div><small>Deals won</small><strong>19</strong></div>
              </div>
              <div class="aa-mock-chart">
                <i style="height:38%"></i><i style="height:55%"></i><i style="height:44%"></i>
                <i style="height:70%"></i><i style="height:62%"></i><i style="height:86%"></i>
                <i style="height:74%"></i>
              </div>
            </div>
          </div>
          </div>

          <ul class="aa-features list-unstyled">
            <li v-for="(f, i) in features" :key="f.title" class="aa-feature" :style="{ '--i': i }">
              <span class="aa-feature-icon"><i class="ti" :class="f.icon"></i></span>
              <div>
                <div class="aa-feature-title">{{ f.title }}</div>
                <div class="aa-feature-text">{{ f.text }}</div>
              </div>
            </li>
          </ul>

          <ul class="aa-stats list-unstyled">
            <li v-for="(s, i) in stats" :key="s.value" :style="{ '--i': i }">
              <i class="ti" :class="s.icon"></i>
              <span><strong>{{ s.value }}</strong> {{ s.label }}</span>
            </li>
          </ul>
        </div>
      </aside>

      <!-- ============ RIGHT: LOGIN ============ -->
      <main class="col-12 col-lg-6 aa-login">
        <div class="aa-login-inner">
          <form class="aa-card" novalidate @submit.prevent="submitForm">
            <div class="text-center mb-4 aa-stagger" style="--i:0">
              <img src="@/assets/img/logo.webp" class="aa-logo" alt="AlgoCRM" />
            </div>

            <div class="text-center mb-4">
              <h2 class="aa-form-title">Welcome back</h2>
              <p class="aa-form-subtitle mb-0">Sign in to your account to continue to AlgoCRM</p>
            </div>

            <!-- Email -->
            <div class="mb-3">
              <label for="login_email" class="form-label aa-label">Email address</label>
              <div class="aa-input-group">
                <span class="aa-input-icon-left" aria-hidden="true"><i class="ti ti-mail"></i></span>
                <input
                  id="login_email"
                  type="email"
                  v-model="email"
                  class="form-control aa-input"
                  :class="{ 'is-invalid-aa': errors.email }"
                  placeholder="you@company.com"
                  autocomplete="username"
                  :aria-invalid="errors.email ? 'true' : 'false'"
                />
              </div>
              <div v-if="errors.email" class="aa-error">{{ errors.email[0] }}</div>
            </div>

            <!-- Password -->
            <div class="mb-3">
              <label for="login_password" class="form-label aa-label">Password</label>
              <div class="aa-input-group">
                <span class="aa-input-icon-left" aria-hidden="true"><i class="ti ti-lock"></i></span>
                <input
                  id="login_password"
                  :type="showPassword ? 'text' : 'password'"
                  v-model="password"
                  class="form-control aa-input aa-input-has-toggle"
                  :class="{ 'is-invalid-aa': errors.password }"
                  placeholder="Enter your password"
                  autocomplete="current-password"
                  :aria-invalid="errors.password ? 'true' : 'false'"
                />
                <button
                  type="button"
                  class="aa-input-toggle"
                  :aria-label="buttonLabel + ' password'"
                  :aria-pressed="showPassword"
                  @click="toggleShow"
                >
                  <i class="ti" :class="showPassword ? 'ti-eye' : 'ti-eye-off'"></i>
                </button>
              </div>
              <div v-if="errors.password" class="aa-error">{{ errors.password[0] }}</div>
            </div>

            <!-- Remember me -->
            <div class="form-check mb-4">
              <input class="form-check-input aa-checkbox" id="remember_me" type="checkbox" />
              <label for="remember_me" class="form-check-label aa-check-label">Remember me</label>
            </div>

            <!-- Submit -->
            <button type="submit" class="btn aa-btn-signin w-100">
              Sign In
              <i class="ti ti-arrow-right ms-1"></i>
            </button>
          </form>

          <p class="aa-copyright">Copyright &copy; 2025 - Algo Ads Private Limited</p>
        </div>
      </main>
    </div>
  </div>
</template>

<style scoped>
.aa-page {
  --aa-ink: #1e1b4b;
  --aa-muted: #6b7280;
  --aa-blue: #075fad;
  --aa-violet: #7c5cff;
  --aa-mint: #5eead4;
  --aa-border: #e3e8f2;
  --aa-radius: 14px;
  background: #fff;
  color: var(--aa-ink);
}

/* ---------- Hero ---------- */
.aa-hero {
  position: relative;
  overflow: hidden;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 3rem;
  background:
    radial-gradient(ellipse at 0% 0%, #e6f0ff 0%, transparent 55%),
    radial-gradient(ellipse at 100% 100%, #efe9ff 0%, transparent 55%),
    linear-gradient(160deg, #f5f9ff 0%, #eefcf8 100%);
  border-right: 1px solid var(--aa-border);
}

.aa-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.45;
  pointer-events: none;
}
.aa-blob-mint { width: 280px; height: 280px; top: -90px; right: -60px; background: var(--aa-mint); }
.aa-blob-violet { width: 300px; height: 300px; bottom: -110px; left: -80px; background: #b9a7ff; }

.aa-hero-inner {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 560px;
}

.aa-brand { display: flex; align-items: center; gap: 0.6rem; margin: 1.1rem 0 1.75rem; }
.aa-brand-mark {
  width: 38px; height: 38px;
  display: grid; place-items: center;
  border-radius: 12px;
  color: #fff; font-size: 1.25rem;
  background: linear-gradient(135deg, var(--aa-blue), var(--aa-violet));
  box-shadow: 0 6px 16px rgba(99, 102, 241, 0.3);
}
.aa-brand-name { font-size: 1.9rem; font-weight: 700; letter-spacing: -0.3px; line-height: 1; white-space: nowrap; }
.aa-letter { position: relative; display: inline-block; }
.aa-letter.is-accent { color: var(--aa-blue); font-weight: 800; font-size: 20px; }

.aa-hero-title {
  font-size: clamp(1.7rem, 2.4vw, 2.3rem);
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.5px;
  margin-bottom: 0.75rem;
}
.aa-hero-text { color: var(--aa-muted); font-size: 0.98rem; line-height: 1.6; margin-bottom: 1.5rem; max-width: 480px; }

/* Dashboard illustration */
.aa-mock {
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid var(--aa-border);
  border-radius: 16px;
  box-shadow: 0 18px 40px -18px rgba(79, 92, 200, 0.35);
  overflow: hidden;
  margin-bottom: 1.25rem;
}
.aa-mock-bar { display: flex; gap: 6px; padding: 10px 14px; border-bottom: 1px solid var(--aa-border); }
.aa-mock-bar span { width: 8px; height: 8px; border-radius: 50%; background: #d8dff0; }
.aa-mock-body { padding: 14px; display: grid; grid-template-columns: 1fr 1.1fr; gap: 14px; align-items: end; }
.aa-mock-kpis { display: grid; gap: 8px; }
.aa-mock-kpis div {
  display: flex; justify-content: space-between; align-items: baseline;
  padding: 8px 10px; border-radius: 10px; background: #f3f7ff;
}
.aa-mock-kpis small { color: var(--aa-muted); font-size: 0.72rem; }
.aa-mock-kpis strong { font-size: 0.95rem; }
.aa-mock-chart { display: flex; align-items: flex-end; gap: 6px; height: 104px; }
.aa-mock-chart i {
  flex: 1;
  border-radius: 6px 6px 3px 3px;
  background: linear-gradient(180deg, var(--aa-violet), var(--aa-blue));
  opacity: 0.85;
}
.aa-mock-chart i:nth-child(even) { background: linear-gradient(180deg, #7cecd8, var(--aa-blue)); }

/* Feature cards */
.aa-features { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin: 0 0 1.25rem; padding: 0; }
.aa-feature {
  display: flex; gap: 10px; align-items: flex-start;
  padding: 12px;
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid var(--aa-border);
  border-radius: 12px;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.aa-feature:hover { border-color: #c9d5f5; box-shadow: 0 6px 16px -8px rgba(79, 92, 200, 0.3); }
.aa-feature-icon {
  flex: 0 0 34px; height: 34px;
  display: grid; place-items: center;
  border-radius: 10px;
  font-size: 1.1rem;
  color: var(--aa-violet);
  background: linear-gradient(135deg, #e8f0ff, #e7fbf5);
}
.aa-feature-title { font-size: 0.86rem; font-weight: 600; line-height: 1.3; }
.aa-feature-text { font-size: 0.76rem; color: var(--aa-muted); line-height: 1.4; margin-top: 2px; }

/* Stats */
.aa-stats {
  display: flex; flex-wrap: wrap; gap: 1.25rem;
  margin: 0; padding: 1rem 0 0;
  border-top: 1px solid var(--aa-border);
}
.aa-stats li { display: flex; align-items: center; gap: 8px; font-size: 0.85rem; color: var(--aa-muted); }
.aa-stats i { color: var(--aa-blue); font-size: 1.15rem; }
.aa-stats strong { color: var(--aa-ink); font-weight: 700; }

/* ---------- Login ---------- */
.aa-login {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  min-height: 100vh;
  background:
    radial-gradient(ellipse at 100% 0%, #f1ecff 0%, transparent 45%),
    #ffffff;
}
.aa-login-inner { width: 100%; max-width: 420px; }

.aa-card { padding: 0.5rem 0.25rem; }
.aa-logo { width: 100%; max-width: 170px; height: auto; }

.aa-form-title { font-size: 1.65rem; font-weight: 700; letter-spacing: -0.3px; color: var(--aa-ink); margin-bottom: 0.4rem; }
.aa-form-subtitle { color: var(--aa-muted); font-size: 0.92rem; }

.aa-label { font-size: 0.85rem; font-weight: 600; color: #374151; margin-bottom: 6px; }

.aa-input-group { position: relative; display: flex; align-items: center; }
.aa-input-icon-left {
  position: absolute; left: 14px; z-index: 2;
  display: flex; align-items: center;
  color: #9aa3b5; font-size: 1.1rem;
  pointer-events: none;
}
.aa-input {
  height: 48px;
  padding-left: 42px;
  border: 1.5px solid var(--aa-border);
  border-radius: 12px;
  font-size: 0.92rem;
  color: #1f2937;
  background: #f8faff;
  transition: border-color 0.2s, background-color 0.2s, box-shadow 0.2s;
}
.aa-input-has-toggle { padding-right: 44px; }
.aa-input::placeholder { color: #aab2c3; }
.aa-input:hover { border-color: #cfd8ee; }
.aa-input:focus {
  border-color: var(--aa-blue);
  background: #fff;
  box-shadow: 0 0 0 4px rgba(79, 124, 255, 0.14);
  outline: none;
}
.is-invalid-aa, .is-invalid-aa:focus { border-color: #ef4444; box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1); }

.aa-input-toggle {
  position: absolute; right: 8px; z-index: 2;
  width: 34px; height: 34px;
  display: grid; place-items: center;
  border: 0; border-radius: 9px;
  background: transparent;
  color: #9aa3b5; font-size: 1.1rem;
  cursor: pointer;
  transition: color 0.2s, background-color 0.2s;
}
.aa-input-toggle:hover { color: var(--aa-blue); background: #eef3ff; }
.aa-input-toggle:focus-visible { outline: 2px solid var(--aa-blue); outline-offset: 1px; }

.aa-error { color: #dc2626; font-size: 0.8rem; margin-top: 6px; }

.aa-checkbox { cursor: pointer; border-color: #c5cee3; }
.aa-checkbox:checked { background-color: var(--aa-blue); border-color: var(--aa-blue); }
.aa-checkbox:focus { box-shadow: 0 0 0 4px rgba(79, 124, 255, 0.18); border-color: var(--aa-blue); }
.aa-check-label { font-size: 0.85rem; color: #4b5563; cursor: pointer; }

.aa-btn-signin {
  height: 48px;
  border: 0;
  border-radius: 12px;
  color: #fff;
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: 0.2px;
  background: linear-gradient(135deg, var(--aa-blue) 0%, var(--aa-violet) 100%);
  box-shadow: 0 8px 20px -8px rgba(99, 102, 241, 0.6);
  transition: transform 0.15s, box-shadow 0.2s, filter 0.2s;
}
.aa-btn-signin:hover { color: #fff; filter: brightness(1.05); transform: translateY(-1px); box-shadow: 0 12px 24px -8px rgba(99, 102, 241, 0.6); }
.aa-btn-signin:active { color: #fff; transform: translateY(0); box-shadow: 0 4px 12px -6px rgba(99, 102, 241, 0.6); }
.aa-btn-signin:focus-visible { color: #fff; outline: 3px solid rgba(79, 124, 255, 0.35); outline-offset: 2px; }

.aa-copyright { margin: 2rem 0 0; text-align: center; font-size: 0.8rem; color: #9aa3b5; }


/* ---------- Animations ---------- */
@keyframes aa-rise { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: none; } }
@keyframes aa-drift { 0%, 100% { transform: translate(0, 0) scale(1); } 50% { transform: translate(24px, -18px) scale(1.08); } }
@keyframes aa-grow { from { transform: scaleY(0); } to { transform: scaleY(1); } }
@keyframes aa-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
@keyframes aa-pulse { 0% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.5); } 70%, 100% { box-shadow: 0 0 0 7px rgba(34, 197, 94, 0); } }
@keyframes aa-pop { from { opacity: 0; transform: scale(0.85) translateY(8px); } to { opacity: 1; transform: none; } }

/* One orchestrated entrance for the hero */
.aa-hero-inner > .aa-hero-title,
.aa-hero-inner > .aa-hero-text,
.aa-hero-inner > .aa-mock-wrap,
.aa-hero-inner > .aa-stats { animation: aa-rise 0.7s cubic-bezier(0.22, 1, 0.36, 1) both; }
.aa-hero-inner > .aa-hero-title { animation-delay: 0.15s; }
.aa-hero-inner > .aa-hero-text { animation-delay: 0.25s; }
.aa-hero-inner > .aa-mock-wrap { animation-delay: 0.35s; }
.aa-hero-inner > .aa-stats { animation-delay: 0.9s; }
.aa-feature { animation: aa-rise 0.6s cubic-bezier(0.22, 1, 0.36, 1) both; animation-delay: calc(0.55s + var(--i) * 0.08s); }
.aa-stagger { animation: aa-rise 0.7s cubic-bezier(0.22, 1, 0.36, 1) both; }

/* Login card entrance */
.aa-card > * { animation: aa-rise 0.6s cubic-bezier(0.22, 1, 0.36, 1) both; }
.aa-card > *:nth-child(1) { animation-delay: 0.05s; }
.aa-card > *:nth-child(2) { animation-delay: 0.12s; }
.aa-card > *:nth-child(3) { animation-delay: 0.19s; }
.aa-card > *:nth-child(4) { animation-delay: 0.26s; }
.aa-card > *:nth-child(5) { animation-delay: 0.33s; }
.aa-card > *:nth-child(6) { animation-delay: 0.4s; }
.aa-copyright { animation: aa-rise 0.6s 0.5s both; }

/* Ambient motion */
.aa-blob-mint { animation: aa-drift 14s ease-in-out infinite; }
.aa-blob-violet { animation: aa-drift 18s ease-in-out infinite reverse; }
.aa-brand-mark { transition: transform 0.3s; }
.aa-brand:hover .aa-brand-mark { transform: rotate(-8deg) scale(1.06); }

/* Dashboard illustration */
.aa-mock-wrap { position: relative; margin-bottom: 1.25rem; }
.aa-mock-wrap .aa-mock { margin-bottom: 0; }
.aa-mock-chart i { transform-origin: bottom; animation: aa-grow 0.8s cubic-bezier(0.22, 1, 0.36, 1) both; }
.aa-mock-chart i:nth-child(1) { animation-delay: 0.7s; } .aa-mock-chart i:nth-child(2) { animation-delay: 0.78s; }
.aa-mock-chart i:nth-child(3) { animation-delay: 0.86s; } .aa-mock-chart i:nth-child(4) { animation-delay: 0.94s; }
.aa-mock-chart i:nth-child(5) { animation-delay: 1.02s; } .aa-mock-chart i:nth-child(6) { animation-delay: 1.1s; }
.aa-mock-chart i:nth-child(7) { animation-delay: 1.18s; }
.aa-live { margin-left: auto; display: flex; align-items: center; gap: 6px; font-style: normal; font-size: 0.7rem; color: var(--aa-muted); }
.aa-live b { width: 7px; height: 7px; border-radius: 50%; background: #22c55e; animation: aa-pulse 1.8s infinite; }

.aa-chip {
  position: absolute; z-index: 3;
  display: flex; align-items: center; gap: 6px;
  padding: 7px 12px;
  font-size: 0.76rem; font-weight: 600;
  background: #fff;
  border: 1px solid var(--aa-border);
  border-radius: 999px;
  box-shadow: 0 10px 24px -10px rgba(79, 92, 200, 0.45);
  opacity: 0;
  animation: aa-pop 0.5s both, aa-float 5s ease-in-out infinite;
}
.aa-chip i { font-size: 1rem; }
.aa-chip-a { top: -14px; right: -10px; color: var(--aa-blue); animation-delay: 1.3s, 1.8s; }
.aa-chip-b { bottom: -14px; left: -10px; color: #16a34a; animation-delay: 1.6s, 2.4s; }

/* Hover / focus polish */
.aa-feature { transition: transform 0.25s, border-color 0.2s, box-shadow 0.2s; }
.aa-feature:hover { transform: translateY(-3px); }
.aa-feature:hover .aa-feature-icon { background: linear-gradient(135deg, var(--aa-blue), var(--aa-violet)); color: #fff; }
.aa-feature-icon { transition: background 0.25s, color 0.25s; }
.aa-input-group:focus-within .aa-input-icon-left { color: var(--aa-blue); }
.aa-input-icon-left { transition: color 0.2s; }
.aa-btn-signin { background-size: 200% 100%; background-position: 0 0; position: relative; overflow: hidden; }
.aa-btn-signin:hover { background-position: 100% 0; }
.aa-btn-signin i { display: inline-block; transition: transform 0.2s; }
.aa-btn-signin:hover i { transform: translateX(4px); }
.aa-btn-signin::after {
  content: ''; position: absolute; inset: 0;
  background: linear-gradient(110deg, transparent 30%, rgba(255, 255, 255, 0.35) 50%, transparent 70%);
  transform: translateX(-120%);
}
.aa-btn-signin:hover::after { transform: translateX(120%); transition: transform 0.7s; }


/* ---------- Sequential border animation on feature cards ---------- */
@property --aa-angle { syntax: '<angle>'; inherits: false; initial-value: 0deg; }

@keyframes aa-border-run {
  0%   { --aa-angle: 0deg;   opacity: 0; }
  3%   { opacity: 1; }
  22%  { opacity: 1; }
  25%  { --aa-angle: 360deg; opacity: 0; }
  100% { --aa-angle: 360deg; opacity: 0; }
}

.aa-feature { position: relative; }
.aa-feature::before {
  content: '';
  position: absolute;
  inset: -1px;
  padding: 2px;
  border-radius: 13px;
  pointer-events: none;
  opacity: 0;
  background: conic-gradient(
    from var(--aa-angle),
    transparent 0%, transparent 55%,
    var(--aa-mint) 75%, var(--aa-blue) 90%, var(--aa-violet) 100%
  );
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask: linear-gradient(#000 0 0) content-box exclude, linear-gradient(#000 0 0);
  /* 4 cards x 2s = 8s loop; each card gets its own 2s turn */
  animation: aa-border-run 8s linear infinite;
  animation-delay: calc(1.6s + var(--i) * 2s);
}

/* ---------- Brand: letters fall in, then arrow keeps drawing over "g" ---------- */
@keyframes aa-letter-fall {
  0%   { opacity: 0; transform: translateY(-70px); }
  55%  { opacity: 1; transform: translateY(6px); }
  75%  { transform: translateY(-3px); }
  100% { opacity: 1; transform: translateY(0); }
}
@keyframes aa-arrow-draw {
  0%   { stroke-dashoffset: 1; opacity: 1; }
  40%  { stroke-dashoffset: 0; opacity: 1; }
  80%  { stroke-dashoffset: 0; opacity: 1; }
  95%  { stroke-dashoffset: 0; opacity: 0; }
  100% { stroke-dashoffset: 1; opacity: 0; }
}

.aa-letter { animation: aa-letter-fall 0.7s cubic-bezier(0.22, 1, 0.36, 1) both; animation-delay: calc(0.15s + var(--i) * 0.09s); font-size: 38px;}

.aa-arrow {
  position: absolute;
  left: -0.05em;
  bottom: 0.62em;
  width: 30px;
  height: auto;
  overflow: visible;
  pointer-events: none;
}
.aa-arrow path {
  stroke-dasharray: 1;
  stroke-dashoffset: 0;
  /* starts after the last letter has landed (~1.7s), then repeats */
  animation: aa-arrow-draw 3.6s ease-in-out infinite;
  animation-delay: 1.7s;
  opacity: 0;
}

/* ---------- Responsive ---------- */
@media (max-width: 1399.98px) {
  .aa-hero { padding: 2rem; }
}
@media (max-width: 1199.98px) {
  .aa-features { grid-template-columns: 1fr; }
}
@media (max-height: 760px) and (min-width: 992px) {
  .aa-mock { display: none; }
}
@media (max-width: 575.98px) {
  .aa-login { padding: 1.25rem 1rem; align-items: flex-start; padding-top: 3rem; }
  .aa-form-title { font-size: 1.45rem; }
}

@media (prefers-reduced-motion: reduce) {
  .aa-page *, .aa-page *::before, .aa-page *::after {
    animation: none !important;
    transition: none !important;
  }
  .aa-chip { opacity: 1; }
  .aa-arrow path { opacity: 1; }
}
</style>